import React, { Component } from 'react';
import styled from 'styled-components';
import Card from '../../../components/Card';
import CardHeader from '../../../components/CardHeader';
import ExportButton from '../../../components/Export';
import BarGraph from '../../../components/Charts/BarGraph';
import Select from '../../../components/Select';
import get from 'lodash/get';
import EvolutionIcon from '../../../components/EvolutionIcon';
import { I18n } from 'react-redux-i18n';

const AuctionBarGraphContainer = styled.div`
  .rightHeaderContainer {
    display: flex;
    flex-direction: row;
    align-items: center;
    font-family: ${(props) => props.theme.fonts.sfcompact};
    font-size: 12px;
  }

  .rightHeaderContainer > :not(:last-child) {
    margin-right: 20px;
  }
`;

const Tooltip = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  min-width: 300px;

  .headerTooltip {
    text-transform: uppercase;
    font-size: 12px;
    font-family: ${(props) => props.theme.fonts.sfcompact_semibold};
    color: #abb5be;
    margin-bottom: 20px;
  }

  .tooltipContentContainer {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: 30px;
  }

  .tooltipPriceLabel {
    text-transform: uppercase;
    margin-bottom: 5px;
    font-size: 12px;
    font-family: ${(props) => props.theme.fonts.sfcompact_semibold};
  }

  .tooltipValueContainer {
    display: flex;
    flex-direction: row;

    p {
      margin-left: 5px;
    }
  }
`;

class AuctionBarGraph extends Component {
  constructor(props) {
    super(props);
    this.state = { startVintage: null, endVintage: null };
  }

  getVintageOptions() {
    const { data = [] } = this.props;
    return [...new Set(data.map((item) => item.bottle_vintage))]
      .sort()
      .reverse()
      .map((item) => ({ label: item, value: item }));
  }

  renderRightHeader() {
    const vintageOptions = this.getVintageOptions();
    const { startVintage, endVintage } = this.state;
    const { exportName } = this.props;
    return (
      <div className="rightHeaderContainer ignored-export">
        <p>{I18n.t('auction.auction.vintageChoice')}</p>
        <Select
          value={startVintage}
          onChange={(value) => this.setState({ startVintage: value })}
          options={vintageOptions}
          placeholder={I18n.t('placeholderAll')}
          dropdownProps={{
            styles: `margin-left:-25px`
          }}
        />
        <p> - </p>
        <Select
          value={endVintage}
          onChange={(value) => this.setState({ endVintage: value })}
          options={vintageOptions}
          placeholder={I18n.t('placeholderAll')}
          dropdownProps={{
            styles: `margin-left:-25px`
          }}
        />
        <ExportButton
          jpg
          pdf
          targetElement="#auction-prix-moyen"
          name={`${exportName}-${I18n.t('auction.auction.priceGraph.title')}`}
        />
      </div>
    );
  }

  filterData() {
    const { data = [] } = this.props;
    const { startVintage, endVintage } = this.state;
    return data.filter(
      (item) =>
        (!startVintage || startVintage.value <= Number(item.bottle_vintage)) &&
        (!endVintage || endVintage.value >= Number(item.bottle_vintage))
    );
  }

  formatData(data) {
    const { currency = 'EUR' } = this.props;
    return data.map((item) => ({
      ...item,
      name: item.bottle_vintage,
      minPrice:
        currency === item.displayed_currency
          ? item.min_price
          : item.min_local_price,
      value:
        currency === item.displayed_currency
          ? item.avg_price
          : item.avg_local_price,
      maxPrice:
        currency === item.displayed_currency
          ? item.max_price
          : item.max_local_price
    }));
  }

  renderTooltip(hoverData) {
    const { currency = 'EUR' } = this.props;
    return (
      <Tooltip>
        <p className="headerTooltip">
          {get(hoverData, '[0].wine_name')}{' '}
          {get(hoverData, '[0].bottle_vintage')} -{' '}
          {get(hoverData, '[0].quantity')} bt.
        </p>
        <div className="tooltipContentContainer">
          <div className="tooltipPriceContainer">
            <p className="tooltipPriceLabel">{I18n.t('lowPrice')}</p>
            <div className="tooltipValueContainer">
              <EvolutionIcon icon="down" />
              <p>
                {Number(get(hoverData, '[0].minPrice', 0)).toLocaleString(
                  'fr-FR',
                  {
                    style: 'currency',
                    currency: currency,
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1
                  }
                )}
              </p>
            </div>
          </div>
          <div className="tooltipPriceContainer">
            <p className="tooltipPriceLabel">{I18n.t('averagePrice')}</p>
            <div className="tooltipValueContainer">
              <EvolutionIcon icon="neutral" />
              <p>
                {Number(get(hoverData, '[0].value', 0)).toLocaleString(
                  'fr-FR',
                  {
                    style: 'currency',
                    currency: currency,
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1
                  }
                )}
              </p>
            </div>
          </div>
          <div className="tooltipPriceContainer">
            <p className="tooltipPriceLabel">{I18n.t('highPrice')}</p>
            <div className="tooltipValueContainer">
              <EvolutionIcon icon="up" />
              <p>
                {Number(get(hoverData, '[0].maxPrice', 0)).toLocaleString(
                  'fr-FR',
                  {
                    style: 'currency',
                    currency: currency,
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1
                  }
                )}
              </p>
            </div>
          </div>
        </div>
      </Tooltip>
    );
  }

  render() {
    const { currency = 'EUR' } = this.props;
    const filteredData = this.filterData();
    const data = [this.formatData(filteredData)];
    return (
      <AuctionBarGraphContainer>
        <Card id="auction-prix-moyen">
          <CardHeader
            fontSize="24px"
            title={I18n.t('auction.auction.priceGraph.title')}
            alignItems="center"
            rightComponents={this.renderRightHeader()}
          />
          <BarGraph
            height={350}
            data={data}
            currency={currency}
            tooltipOffset={{ x: 15, y: 0 }}
            renderTooltip={(hoverData) => this.renderTooltip(hoverData)}
            labels={[
              `${get(data, '[0][0].wine_name')} ${I18n.t(
                'auction.auction.priceGraph.title'
              )}`
            ]}
            yAxisProps={{
              type: 'number',
              tickFormatter: (value) =>
                value.toLocaleString('fr-FR', {
                  style: 'currency',
                  currency,
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                })
            }}
          />
        </Card>
      </AuctionBarGraphContainer>
    );
  }
}

export default AuctionBarGraph;
